import {
  QUOTATION_STORAGE_KEY,
  QUOTATION_STORAGE_VERSION,
  QUOTATION_TAB,
  defaultQuotationRecords,
  generatedLoadQuotationTemplates,
} from "./quotation.constants";

const clone = (value) => JSON.parse(JSON.stringify(value));

export const safeArray = (value) => (Array.isArray(value) ? value : []);
export const normalize = (value) => String(value || "").trim().toLowerCase();

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toDateTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  const time = parsed.getTime();
  return Number.isFinite(time) ? time : null;
};

export const formatAmount = (value, fallback = "-") => {
  const parsed = toNumber(value);
  if (parsed === null) return fallback;
  return parsed.toLocaleString();
};

export const readStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

export const writeStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const loadQuotationRecords = () => {
  const stored = readStorage(QUOTATION_STORAGE_KEY, null);
  if (!stored || typeof stored !== "object") {
    const seeded = clone(defaultQuotationRecords);
    writeStorage(QUOTATION_STORAGE_KEY, {
      version: QUOTATION_STORAGE_VERSION,
      records: seeded,
    });
    return seeded;
  }

  if (
    stored.version !== QUOTATION_STORAGE_VERSION ||
    !stored.records ||
    typeof stored.records !== "object"
  ) {
    const seeded = clone(defaultQuotationRecords);
    writeStorage(QUOTATION_STORAGE_KEY, {
      version: QUOTATION_STORAGE_VERSION,
      records: seeded,
    });
    return seeded;
  }

  return {
    [QUOTATION_TAB.customer]: safeArray(stored.records[QUOTATION_TAB.customer]),
    [QUOTATION_TAB.transporter]: safeArray(stored.records[QUOTATION_TAB.transporter]),
  };
};

export const saveQuotationRecords = (recordsByTab) => {
  writeStorage(QUOTATION_STORAGE_KEY, {
    version: QUOTATION_STORAGE_VERSION,
    records: recordsByTab,
  });
};

const getFilterField = (row, filterName) => {
  switch (filterName) {
    case "loadIdFilter":
      return row.loadId;
    case "partyNameFilter":
      return row.partyName;
    case "pocNameFilter":
      return row.pocName;
    case "pocContactFilter":
      return row.pocContact;
    case "buyingPriceFilter":
      return row.buyingPrice;
    case "quotationDateFilter":
      return row.quotationDate;
    case "loadingPointFilter":
      return row.loadingPoint;
    default:
      return "";
  }
};

const hasEmptyFilterValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const isEqualComparison = (left, right) =>
  normalize(left) === normalize(right) || String(left) === String(right);

const isLessComparison = (left, right) => {
  const leftNumber = toNumber(left);
  const rightNumber = toNumber(right);
  if (leftNumber !== null && rightNumber !== null) return leftNumber < rightNumber;

  const leftDate = toDateTimestamp(left);
  const rightDate = toDateTimestamp(right);
  if (leftDate !== null && rightDate !== null) return leftDate < rightDate;

  return false;
};

const isGreaterComparison = (left, right) => {
  const leftNumber = toNumber(left);
  const rightNumber = toNumber(right);
  if (leftNumber !== null && rightNumber !== null) return leftNumber > rightNumber;

  const leftDate = toDateTimestamp(left);
  const rightDate = toDateTimestamp(right);
  if (leftDate !== null && rightDate !== null) return leftDate > rightDate;

  return false;
};

const matchesFilter = (value, filterObj) => {
  const filterType = filterObj?.filter_type;
  const filterValue = filterObj?.value;

  if (!filterType || hasEmptyFilterValue(filterValue)) return true;

  if (filterType === "one_of") {
    const list = safeArray(filterValue);
    if (!list.length) return true;
    return list.some((item) => isEqualComparison(value, item));
  }

  if (filterType === "contains") {
    return normalize(value).includes(normalize(filterValue));
  }

  if (filterType === "equals" || filterType === "equal_to") {
    return isEqualComparison(value, filterValue);
  }

  if (filterType === "less_than") {
    return isLessComparison(value, filterValue);
  }

  if (filterType === "greater_than") {
    return isGreaterComparison(value, filterValue);
  }

  if (filterType === "less_than_equal_to") {
    return isLessComparison(value, filterValue) || isEqualComparison(value, filterValue);
  }

  if (filterType === "greater_than_equal_to") {
    return isGreaterComparison(value, filterValue) || isEqualComparison(value, filterValue);
  }

  return true;
};

export const applyQuotationFilters = (rows, searchFilters = {}) => {
  if (!searchFilters || !Object.keys(searchFilters).length) return rows;

  return safeArray(rows).filter((row) =>
    Object.entries(searchFilters).every(([filterName, filterObj]) =>
      matchesFilter(getFilterField(row, filterName), filterObj)
    )
  );
};

export const searchQuotationRows = (rows, searchText) => {
  const keyword = normalize(searchText);
  if (!keyword) return safeArray(rows);

  return safeArray(rows).filter((row) => {
    const text = [
      row.loadId,
      row.partyName,
      row.pocName,
      row.pocContact,
      row.buyingPrice,
      row.quotationDate,
      row.loadingPoint,
    ]
      .map((item) => String(item || ""))
      .join(" ");

    return normalize(text).includes(keyword);
  });
};

export const paginateRows = (rows, page, limit) => {
  const safeLimit = Math.max(1, Number(limit) || 1);
  const safePage = Math.max(1, Number(page) || 1);
  const start = (safePage - 1) * safeLimit;
  return safeArray(rows).slice(start, start + safeLimit);
};

export const generateQuotationId = (tab, rows) => {
  const prefix = tab === QUOTATION_TAB.transporter ? "QTR-" : "QCU-";
  const next = safeArray(rows).reduce((max, row) => {
    const value = Number(String(row?.id || "").replace(prefix, ""));
    if (!Number.isFinite(value)) return max;
    return Math.max(max, value);
  }, 1000);
  return `${prefix}${next + 1}`;
};

export const buildLoadQuotationRows = (loadId, basePrice, quotationDate, loadingPoint) => {
  const price = toNumber(basePrice) || 0;

  return generatedLoadQuotationTemplates.map((template, index) => ({
    id: `${String(loadId || "LDB-0000")}-${String.fromCharCode(65 + index)}`,
    partyName: template.partyName,
    pocName: template.pocName,
    pocContact: template.pocContact,
    buyingPrice: price + index * 5000,
    quotationDate,
    loadingPoint,
  }));
};

export const buildQuotationStats = (recordsByTab = {}) => {
  const customerRows = safeArray(recordsByTab[QUOTATION_TAB.customer]);
  const transporterRows = safeArray(recordsByTab[QUOTATION_TAB.transporter]);
  const allRows = [...customerRows, ...transporterRows];

  const total = allRows.length;
  const customerTotal = customerRows.length;
  const transporterTotal = transporterRows.length;
  const average =
    total > 0
      ? Math.round(
          allRows.reduce((sum, row) => sum + (toNumber(row.buyingPrice) || 0), 0) / total
        )
      : 0;

  const latestDate = allRows
    .map((row) => toDateTimestamp(row.quotationDate))
    .filter((value) => value !== null)
    .sort((a, b) => b - a)[0];

  const safeTotal = total || 1;
  const customerPercentage = `${((customerTotal / safeTotal) * 100).toFixed(0)}%`;
  const transporterPercentage = `${((transporterTotal / safeTotal) * 100).toFixed(0)}%`;

  return {
    total,
    customerTotal,
    transporterTotal,
    average,
    customerPercentage,
    transporterPercentage,
    latestDate: latestDate ? new Date(latestDate).toISOString() : null,
  };
};

export const buildQuotationOverview = (rows = []) => {
  const safeRows = safeArray(rows);
  const total = safeRows.length;
  const totalAmount = safeRows.reduce(
    (sum, row) => sum + (toNumber(row.buyingPrice) || 0),
    0
  );
  const average = total ? Math.round(totalAmount / total) : 0;
  const highest = safeRows.reduce(
    (max, row) => Math.max(max, toNumber(row.buyingPrice) || 0),
    0
  );
  const loadCount = new Set(
    safeRows
      .map((row) => String(row.loadId || "").trim())
      .filter(Boolean)
  ).size;
  const latestDate = safeRows
    .map((row) => toDateTimestamp(row.quotationDate))
    .filter((value) => value !== null)
    .sort((a, b) => b - a)[0];

  return {
    total,
    totalAmount,
    average,
    highest,
    loadCount,
    latestDate: latestDate ? new Date(latestDate).toISOString() : null,
  };
};
